import { useMutation, useQueryClient } from "@tanstack/react-query";
import { extractStatus, normalizeWorkerResponse } from "./helpers";
import { showSuccessToast } from "../../utils/showToastAlert";

/**
 * @param {object} deps
 * @param {(url: string, options: any) => Promise<any>} deps.callApiWithWorker
 * @param {(key: string) => string} deps.t
 * @param {(err: any) => Promise<void> | void} [deps.onErrorAlert]
 */
export function usePutParticipant({ callApiWithWorker, t, onErrorAlert }) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: ["participants", "update"],
    mutationFn: async ({ participantId, payload }) => {
      if (!participantId) throw new Error("Missing participantId");

      const res = await callApiWithWorker(
        `/registry/participants/${encodeURIComponent(participantId)}`,
        {
          method: "PUT",
          connector: "springConnector",
          body: payload,
        },
      );

      const status = extractStatus(res);
      const data = normalizeWorkerResponse(res);

      if (status && status >= 400) {
        const e = new Error(`Update participant failed (${status})`);
        e.statusCode = status;
        e.response = data;
        throw e;
      }

      return data;
    },
    onSuccess: async (_data, vars) => {
      await qc.invalidateQueries({ queryKey: ["participants"] });
      await qc.invalidateQueries({
        queryKey: ["participants", "byId", vars?.participantId],
      });
      showSuccessToast(
        t?.(
          "updateSubMessage",
          "The participant was updated successfully.",
        ),
        {
          title: t?.("updateMessage", "Updated successfully"),
        },
      );
    },
    onError: async (err) => {
      if (onErrorAlert) return onErrorAlert(err);
      console.error(t ? t("error") : "Error", err);
    },
  });
}
